import {
  InventoryListing,
  useUpdateInventoryManualFields,
  useHoldInventory,
  useSetRecommendedPrice,
  useAcknowledgeRecommendedPrice,
  useGetMe,
  getListEventInventoryQueryKey,
  getGetDashboardSummaryQueryKey,
  getListEventsQueryKey,
  useMarkListed,
  useBulkSetBuybackFveIntent,
} from "@workspace/api-client-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ColumnDef,
  ColumnOrderState,
  Row,
  SortingState,
  VisibilityState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  Header,
} from "@tanstack/react-table";
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
  DragOverlay,
  DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { defaultRangeExtractor, useVirtualizer } from "@tanstack/react-virtual";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { BankNumbersPopover } from "@/components/bank-numbers";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PhoneStatusIcon } from "@/components/phone-status-icon";
import { usePersistentState } from "@/hooks/use-persistent-state";
import {
  useResizableColumns,
  ColumnResizeHandle,
  ResetColumnWidthsButton,
} from "@/hooks/use-resizable-columns";
import {
  SlidersHorizontal,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  ChevronRight,
  QrCode,
  Link2,
  TrendingUp,
  Tag,
  OctagonX,
  Repeat2,
  GripVertical,
  RotateCcw,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  NoteCell,
  RowActionsCell,
  FveStatusPill,
  IntentCell,
  PriceCell,
  ListingCell,
  BroadcastCell,
  QrCoverageCell,
  LinkCoverageCell,
  TicketsCell,
  BuybackMarginCell,
  HoldAction,
  ListingActivitySheet,
  ListingEmailsCell,
  effectivePrice,
  seatSummary,
  SeatPill,
  displayQty,
  holdState,
  nat,
  naturalSeatCompare,
  PoChainCell,
  SeatsChainCell,
  AccountInfoPopover,
} from "@/components/inventory-cells";
import { InventoryMobileCard } from "@/components/inventory-card";
import { useIsMobile } from "@/hooks/use-mobile";
import { useFveTransitions } from "@/lib/use-fve-transitions";
import { showHoldOutcomeToast } from "@/lib/hold-toast";

// Re-exported for callers that import holdState alongside InventoryTable.
export { holdState };
export type { HoldState } from "@/components/inventory-cells";

/** Plain password cell — shows the value directly in monospace. */
function PwCell({ value }: { value: string }) {
  return (
    <span className="font-mono text-xs tabular-nums text-foreground truncate">
      {value}
    </span>
  );
}

interface InventoryTableProps {
  data: InventoryListing[];
  eventId: number;
  selectedIds: Set<number>;
  onToggleSelectAll: () => void;
  onSelectIds: (ids: number[], selected: boolean) => void;
  allSelected: boolean;
  defaultHoldDays: number;
  hideGetIn?: boolean;
  /**
   * Grouping mode for the workspace table:
   * - "puller"   — puller → renter → listings (default)
   * - "purchase" — purchase order → listings (buyback events)
   * - "both"     — puller → purchase → listings (both dimensions active)
   * - "flat"     — no grouping, just a flat listing table
   */
  groupBy?: "puller" | "purchase" | "both" | "flat";
}

const columnHelper = createColumnHelper<InventoryListing>();

const ROW_HEIGHT = 37;
const PULLER_HEIGHT = 50;
const RENTER_HEIGHT = 30;
const HEADER_HEIGHT = 36;

const UNASSIGNED_PULLER = "Unassigned";
const NO_RENTER = "No Renter";
const NO_PURCHASE = "No Purchase";
const UNKNOWN_ACCOUNT = "Unknown Account";

// Column id -> CSS grid track. Fixed for compact columns, flexible for text so
// the grid fills the panel width and aligns header/group/data rows perfectly.
const COL_WIDTHS: Record<string, string> = {
  select: "36px",
  actions: "132px",
  fve: "minmax(150px, 0.9fr)",
  seats: "130px",
  qty: "52px",
  price: "120px",
  margin: "minmax(170px, 1fr)",
  listing: "100px",
  broadcast: "84px",
  qr: "40px",
  link: "40px",
  sales: "120px",
  account: "minmax(280px, 2.5fr)",
  puller: "minmax(130px, 1fr)",
  origAcct: "minmax(180px, 1.3fr)",
  poChain: "minmax(200px, 1.6fr)",
  seatsChain: "minmax(260px, 2.2fr)",
  phone: "56px",
  bank: "150px",
  note: "minmax(120px, 1fr)",
  tmPassword: "120px",
  intent: "40px",
};

// Buyback-event-specific default column widths: narrower price/note/phone,
// wider account, medium bank/tickets, moderate actions/status/seats›sale›bb.
const BUYBACK_COL_WIDTHS: Record<string, string> = {
  select: "36px",
  actions: "132px",
  fve: "minmax(140px, 0.9fr)",
  intent: "40px",
  seatsChain: "minmax(200px, 1.5fr)",
  seats: "130px",
  qty: "52px",
  price: "80px",
  margin: "minmax(170px, 1fr)",
  qr: "40px",
  link: "40px",
  sales: "100px",
  account: "minmax(240px, 2.5fr)",
  phone: "56px",
  bank: "120px",
  note: "72px",
  listing: "100px",
  broadcast: "84px",
  puller: "minmax(130px, 1fr)",
  origAcct: "minmax(180px, 1.3fr)",
  poChain: "minmax(200px, 1.6fr)",
  tmPassword: "120px",
};

// Buyback-event-specific default visibility: hide columns not relevant for
// the purchase-oriented workflow. Listing/Broadcast/Puller remain available
// in the Columns picker; operators can re-enable them at any time.
const BUYBACK_DEFAULT_VISIBILITY: VisibilityState = {
  tmPassword: false,
  listing: false,
  broadcast: false,
  puller: false,
  origAcct: false,
  poChain: false,
};

// Default column order — used as the canonical order when splicing in conditional
// columns that are missing from a saved (possibly stale) column-order preference.
const DEFAULT_COLUMN_ORDER: string[] = [
  "select",
  "actions",
  "fve",
  "intent",
  "seatsChain",
  "seats",
  "qty",
  "price",
  "margin",
  "listing",
  "broadcast",
  "qr",
  "link",
  "sales",
  "account",
  "tmPassword",
  "puller",
  "origAcct",
  "poChain",
  "phone",
  "bank",
  "note",
];

/**
 * Draggable column header cell. Renders the sort button, drag handle, and
 * resize handle inside a sortable dnd-kit wrapper.
 */
function SortableHeaderCell({
  header,
  resizable,
}: {
  header: Header<InventoryListing, unknown>;
  resizable: ReturnType<typeof useResizableColumns>;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: header.column.id });

  const canSort = header.column.getCanSort();
  const sorted = header.column.getIsSorted();

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 20 : undefined,
    position: "relative",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative flex items-center px-3 text-[10px] uppercase tracking-wider text-muted-foreground font-mono font-medium whitespace-nowrap select-none overflow-hidden group/hdr"
    >
      {/* Drag handle — hidden until header is hovered */}
      <span
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing shrink-0 mr-1 text-muted-foreground/30 hover:text-muted-foreground/70 transition-colors opacity-0 group-hover/hdr:opacity-100"
        title="Drag to reorder column"
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical className="h-3 w-3" />
      </span>

      {header.isPlaceholder ? null : canSort ? (
        <button
          onClick={header.column.getToggleSortingHandler()}
          className="flex items-center gap-1 hover:text-foreground transition-colors uppercase"
        >
          {flexRender(header.column.columnDef.header, header.getContext())}
          {sorted === "asc" ? (
            <ChevronUp className="h-3 w-3" />
          ) : sorted === "desc" ? (
            <ChevronDown className="h-3 w-3" />
          ) : (
            <ChevronsUpDown className="h-3 w-3 opacity-40" />
          )}
        </button>
      ) : (
        flexRender(header.column.columnDef.header, header.getContext())
      )}

      <ColumnResizeHandle columnId={header.column.id} onResize={resizable.startResize} />
    </div>
  );
}

function SortablePickerItem({
  id,
  label,
  checked,
  onCheckedChange,
}: {
  id: string;
  label: string;
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <DropdownMenuCheckboxItem
      ref={setNodeRef}
      style={style}
      checked={checked}
      onCheckedChange={onCheckedChange}
      onSelect={(e) => e.preventDefault()}
      className="text-xs pl-7 pr-2"
    >
      <span
        {...attributes}
        {...listeners}
        className="absolute left-1.5 cursor-grab active:cursor-grabbing text-muted-foreground/40 hover:text-muted-foreground/70 transition-colors"
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical className="h-3 w-3" />
      </span>
      {label}
    </DropdownMenuCheckboxItem>
  );
}

interface PullerMetrics {
  listings: number;
  seats: number;
  held: number;
  good: number;
  bad: number;
}

interface RenterGroup {
  key: string;
  name: string;
  ids: number[];
  rows: InventoryListing[];
}

interface PullerGroup {
  key: string;
  name: string;
  metrics: PullerMetrics;
  ids: number[];
  renters: RenterGroup[];
}

interface PurchaseRow {
  purchaseOrderId: string;
  label: string;
  listingIds: number[];
  /** All constituent listing objects (for seat display, notes, broadcast, etc.). */
  listings: InventoryListing[];
  vendorAccountEmail: string | null;
  totalSeats: number;
  listingCount: number;
  heldCount: number;
  phoneGood: number;
  phoneBad: number;
  phoneUnknown: number;
  /** Uniform intent across all listings, or null if mixed. */
  dominantIntent: "buy_back" | "sell" | "cannot_list" | null;
  /** First listing with bank info (for bank column display). */
  representativeListing: InventoryListing | null;
  /** Per-seat purchase cost (sum of unitCost×qty / totalSeats). */
  perSeatCost: number | null;
  /** Currency code from representative listing. */
  currencyCode: string | null;
  /** True when every listing is broadcast. */
  allBroadcast: boolean;
  /** True when at least one listing is broadcast. */
  anyBroadcast: boolean;
  /** Whether any listing's broadcast state is still unknown (null). */
  someBroadcastUnknown: boolean;
  /** Combined seat display from all constituent listings. */
  seatsDisplay: string;
}

type FlatItem =
  | { kind: "puller"; key: string; group: PullerGroup }
  | { kind: "renter"; key: string; pullerKey: string; group: RenterGroup }
  | { kind: "row"; key: string; row: Row<InventoryListing> }
  | { kind: "purchase"; key: string; pr: PurchaseRow };

// ─── Module-level helper ────────────────────────────────────────────────────
// Computes the purchase-order grouped summary rows from a subset of listings.
// Used both in "purchase" mode (full data) and "both" mode (per-puller slice).
function buildPurchaseRows(data: InventoryListing[]): PurchaseRow[] {
  const buybackTargetPOs = new Set<string>();
  for (const r of data) {
    const bbPo = String(r.buybackBbPurchaseOrderId ?? "").trim();
    if (bbPo) buybackTargetPOs.add(bbPo);
  }
  const poMap = new Map<string, InventoryListing[]>();
  for (const r of data) {
    const key = String(r.purchaseOrderId ?? "").trim() || NO_PURCHASE;
    if (key !== NO_PURCHASE && buybackTargetPOs.has(key)) continue;
    const arr = poMap.get(key);
    if (arr) arr.push(r);
    else poMap.set(key, [r]);
  }
  const poKeys = [...poMap.keys()].sort((a, b) => {
    if (a === NO_PURCHASE && b !== NO_PURCHASE) return 1;
    if (b === NO_PURCHASE && a !== NO_PURCHASE) return -1;
    return nat(a, b);
  });
  return poKeys.map((po) => {
    const rows = poMap.get(po)!;
    const listingIds = rows.map((r) => r.id);
    const email = rows.find((r) => r.vendorAccountEmail)?.vendorAccountEmail ?? null;
    const label =
      po === NO_PURCHASE
        ? email || UNKNOWN_ACCOUNT
        : `${email || UNKNOWN_ACCOUNT}  ·  PO ${po}`;
    let totalSeats = 0;
    let heldCount = 0;
    let phoneGood = 0;
    let phoneBad = 0;
    let phoneUnknown = 0;
    const intents = new Set<string | null>();
    let repListing: InventoryListing | null = null;
    let totalCost = 0;
    let hasAnyUnitCost = false;
    let allBroadcast = true;
    let anyBroadcast = false;
    let someBroadcastUnknown = false;
    for (const r of rows) {
      totalSeats += displayQty(r);
      if (r.adminHoldActive) heldCount += 1;
      if (r.phoneStatus === "good") phoneGood += 1;
      else if (r.phoneStatus === "bad") phoneBad += 1;
      else phoneUnknown += 1;
      intents.add(r.buybackFveIntent ?? null);
      if (!repListing && r.bankMappingStatus) repListing = r;
      if (r.unitCost != null) {
        totalCost += r.unitCost * displayQty(r);
        hasAnyUnitCost = true;
      }
      if (r.reachBroadcast == null) {
        someBroadcastUnknown = true;
      } else if (r.reachBroadcast) {
        anyBroadcast = true;
      } else {
        allBroadcast = false;
      }
      if (!r.reachBroadcast) allBroadcast = false;
    }
    if (!repListing) repListing = rows[0] ?? null;
    if (!anyBroadcast) allBroadcast = false;
    const dominantIntent =
      intents.size === 1
        ? (([...intents][0] as PurchaseRow["dominantIntent"]) ?? null)
        : null;
    const perSeatCost =
      hasAnyUnitCost && totalSeats > 0 ? totalCost / totalSeats : null;
    const seatsDisplay =
      rows.map((r) => seatSummary(r)).filter((s) => s !== "-").join("  ·  ") || "-";
    return {
      purchaseOrderId: po,
      label,
      listingIds,
      listings: rows,
      vendorAccountEmail: email,
      totalSeats,
      listingCount: rows.length,
      heldCount,
      phoneGood,
      phoneBad,
      phoneUnknown,
      dominantIntent,
      representativeListing: repListing,
      perSeatCost,
      currencyCode: repListing?.currencyCode ?? null,
      allBroadcast,
      anyBroadcast,
      someBroadcastUnknown,
      seatsDisplay,
    };
  });
}


export function InventoryTable({
  data,
  eventId,
  selectedIds,
  onToggleSelectAll,
  onSelectIds,
  allSelected,
  defaultHoldDays,
  hideGetIn = false,
  groupBy = "puller",
}: InventoryTableProps) {
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: getListEventInventoryQueryKey(eventId) });
    queryClient.invalidateQueries({ queryKey: getGetDashboardSummaryQueryKey() });
    queryClient.invalidateQueries({ queryKey: getListEventsQueryKey() });
  };

  const manualFields = useUpdateInventoryManualFields({ mutation: { onSuccess: invalidate } });
  const hold = useHoldInventory({
    mutation: {
      onSuccess: (outcome, variables) => {
        invalidate();
        showHoldOutcomeToast(outcome, variables.data.action);
      },
    },
  });
  const fve = useFveTransitions(invalidate);

  const bulkSetFveIntent = useBulkSetBuybackFveIntent({
    mutation: {
      onMutate: (variables) => {
        const ids = new Set(variables.data.ids ?? []);
        const intent = variables.data.intent ?? null;
        const prev = queryClient.getQueryData<InventoryListing[]>(
          getListEventInventoryQueryKey(eventId),
        );
        queryClient.setQueryData<InventoryListing[]>(
          getListEventInventoryQueryKey(eventId),
          (rows) =>
            rows?.map((row) =>
              ids.has(row.id) ? { ...row, buybackFveIntent: intent ?? undefined } : row,
            ),
        );
        return { prev };
      },
      onError: (_err, variables, context) => {
        const ctx = context as { prev?: InventoryListing[] } | undefined;
        if (ctx?.prev !== undefined) {
          queryClient.setQueryData(getListEventInventoryQueryKey(eventId), ctx.prev);
        }
      },
      onSuccess: invalidate,
    },
  });

  const markListedMutation = useMarkListed({
    mutation: { onSuccess: invalidate },
  });

  const { data: me } = useGetMe();
  const isAdmin = me?.role === "admin";
  const myPullerTag = me?.pullerTag ?? null;

  const setRecommended = useSetRecommendedPrice({
    mutation: {
      onSuccess: (res) => {
        invalidate();
        if (res.slackAlert.ok) {
          toast.success("Recommended price saved — puller notified via Slack");
        } else {
          toast.warning(
            `Recommended price saved, but the Slack alert failed${res.slackAlert.error ? `: ${res.slackAlert.error}` : ""}`,
          );
        }
      },
      onError: () => toast.error("Failed to save recommended price"),
    },
  });

  const acknowledgeRecommended = useAcknowledgeRecommendedPrice({
    mutation: {
      onSuccess: () => {
        invalidate();
        toast.success("Acknowledged");
      },
      onError: () => toast.error("Failed to acknowledge"),
    },
  });

  const [sorting, setSorting] = usePersistentState<SortingState>(
    "tex-inv-sorting",
    [],
  );
  // Regular-event layout persistence.
  const [regColumnVisibility, setRegColumnVisibility] = usePersistentState<VisibilityState>(
    "tex-inv-col-visibility",
    { tmPassword: false },
  );
  const [regSavedColumnOrder, setRegSavedColumnOrder] = usePersistentState<string[]>(
    "tex-inv-col-order",
    [],
  );
  const regResizable = useResizableColumns("tex-inv-col-widths", COL_WIDTHS);

  // Buyback-event layout persistence — separate localStorage keys so
  // customising a buyback event never clobbers the regular-event layout.
  const [bbColumnVisibility, setBbColumnVisibility] = usePersistentState<VisibilityState>(
    "tex-inv-bb-col-visibility",
    BUYBACK_DEFAULT_VISIBILITY,
  );
  const [bbSavedColumnOrder, setBbSavedColumnOrder] = usePersistentState<string[]>(
    "tex-inv-bb-col-order",
    [],
  );
  const bbResizable = useResizableColumns("tex-inv-bb-col-widths", BUYBACK_COL_WIDTHS);

  const isMobile = useIsMobile();
  // Collapse sets are persisted to localStorage so operators return to the same
  // view after a reload. Stored as string[] (JSON-serializable) and derived to
  // Set<string> for O(1) lookup. Keys are stable composite strings so they
  // remain meaningful across events (e.g. "John|||No Renter" stays collapsed
  // for any event where that puller / renter combination appears).
  const [collapsedPullersArr, setCollapsedPullersArr] = usePersistentState<string[]>(
    "tex-inv-collapsed-pullers",
    [],
  );
  const [collapsedRentersArr, setCollapsedRentersArr] = usePersistentState<string[]>(
    "tex-inv-collapsed-renters",
    [],
  );
  const collapsedPullers = useMemo(() => new Set(collapsedPullersArr), [collapsedPullersArr]);
  const collapsedRenters = useMemo(() => new Set(collapsedRentersArr), [collapsedRentersArr]);

  const togglePuller = (key: string) =>
    setCollapsedPullersArr((prev) => {
      const s = new Set(prev);
      s.has(key) ? s.delete(key) : s.add(key);
      return [...s];
    });
  const toggleRenter = (key: string) =>
    setCollapsedRentersArr((prev) => {
      const s = new Set(prev);
      s.has(key) ? s.delete(key) : s.add(key);
      return [...s];
    });

  const placeHold = (id: number, holdDays: number) => {
    hold.mutate({ id, data: { action: "hold", holdDays } });
  };

  const releaseHold = (id: number) => {
    hold.mutate({ id, data: { action: "unhold" } });
  };

  const busyId = manualFields.isPending
    ? manualFields.variables?.id
    : hold.isPending
      ? hold.variables?.id
      : (fve.busyId ?? null);

  // An event only ever delivers one of QR (international) or transfer links
  // (domestic), so show only the relevant coverage column. Driven by the event's
  // configured marketType, not by whether assets happen to be uploaded yet, so a
  // freshly imported event with no uploads still shows the correct (possibly
  // empty) column. A genuinely mixed list (multiple events) keeps both.
  const showQrColumn = useMemo(
    () => data.some((r) => r.marketType === "international"),
    [data],
  );
  const showLinkColumn = useMemo(
    () => data.some((r) => r.marketType === "domestic"),
    [data],
  );
  // Show the buyback margin column only when the event has buybacksEnabled=true.
  const showBuybackColumn = useMemo(
    () => data.some((r) => r.buybacksEnabled),
    [data],
  );

  // Select the active layout set based on whether this is a buyback event.
  // Both hook sets are always called (Rules of Hooks); we just pick the right one.
  const columnVisibility = showBuybackColumn ? bbColumnVisibility : regColumnVisibility;
  const setColumnVisibility = showBuybackColumn ? setBbColumnVisibility : setRegColumnVisibility;
  const savedColumnOrder = showBuybackColumn ? bbSavedColumnOrder : regSavedColumnOrder;
  const setSavedColumnOrder = showBuybackColumn ? setBbSavedColumnOrder : setRegSavedColumnOrder;
  const resizable = showBuybackColumn ? bbResizable : regResizable;

  const columns = useMemo<ColumnDef<InventoryListing, unknown>[]>(
    () => [
      columnHelper.display({
        id: "select",
        size: 36,
        enableHiding: false,
        header: () => (
          <Checkbox
            checked={allSelected && data.length > 0}
            onCheckedChange={onToggleSelectAll}
            aria-label="Select all"
            className="opacity-0 group-hover/hdr:opacity-100 data-[state=checked]:opacity-100 data-[state=indeterminate]:opacity-100 transition-opacity"
          />
        ),
        cell: () => null,
      }),
      columnHelper.display({
        id: "actions",
        size: 104,
        enableHiding: false,
        header: () => <div className="pl-1">Actions</div>,
        cell: ({ row }) => {
          const r = row.original;
          return (
            <RowActionsCell
              row={r}
              defaultHoldDays={defaultHoldDays}
              isBusy={busyId === r.id}
              onHold={(days) => placeHold(r.id, days)}
              onUnhold={() => releaseHold(r.id)}
              fve={fve}
            />
          );
        },
      }),
      columnHelper.display({
        id: "fve",
        size: 130,
        enableHiding: false,
        header: () => <div className="pl-1">Status</div>,
        cell: ({ row }) => (
          <FveStatusPill row={row.original} fve={fve} hideGetIn={hideGetIn} />
        ),
      }),
      columnHelper.display({
        id: "intent",
        size: 40,
        enableHiding: false,
        header: () => (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="flex items-center justify-center">
                <Repeat2 className="h-3.5 w-3.5 text-muted-foreground" />
              </span>
            </TooltipTrigger>
            <TooltipContent>Buyback Intent</TooltipContent>
          </Tooltip>
        ),
        cell: ({ row }) => (
          <div className="flex justify-center">
            <IntentCell row={row.original} fve={fve} />
          </div>
        ),
      }),
      columnHelper.display({
        id: "seatsChain",
        header: "Seats › Sale › BB",
        size: 260,
        cell: ({ row }) => (
          <SeatsChainCell
            listings={[row.original]}
            invalidate={invalidate}
          />
        ),
      }),
      columnHelper.accessor((r) => seatSummary(r), {
        id: "seats",
        header: "Seats",
        size: 130,
        sortingFn: (a, b) =>
          naturalSeatCompare(a.original, b.original),
        cell: ({ row }) => {
          const s = seatSummary(row.original);
          return s === "-" ? (
            <span className="text-muted-foreground/50 text-xs">—</span>
          ) : (
            <SeatPill seat={s} />
          );
        },
      }),
      columnHelper.accessor(
        (r) => displayQty(r),
        {
          id: "qty",
          header: "Qty",
          size: 52,
          cell: (info) => (
            <span className="font-mono text-xs">{info.getValue() as number}</span>
          ),
        },
      ),
      columnHelper.accessor((r) => effectivePrice(r).value ?? -1, {
        id: "price",
        header: "Price",
        size: 120,
        cell: ({ row }) => {
          const r = row.original;
          return (
            <PriceCell
              row={r}
              isAdmin={isAdmin}
              canAcknowledge={
                myPullerTag != null && myPullerTag === r.pullerTag
              }
              onSave={(price) =>
                setRecommended.mutateAsync({
                  id: r.id,
                  data: { recommendedListPrice: price },
                })
              }
              onAcknowledge={() =>
                acknowledgeRecommended.mutateAsync({ id: r.id })
              }
            />
          );
        },
      }),
      columnHelper.display({
        id: "margin",
        size: 170,
        header: () => (
          <div className="flex items-center gap-1">
            <TrendingUp className="h-3 w-3 text-muted-foreground" />
            <span>Margin</span>
          </div>
        ),
        cell: ({ row }) => {
          const r = row.original;
          const listedPrice = r.getIn ?? r.listedPrice ?? 0;
          return (
            <BuybackMarginCell
              row={r}
              invalidate={invalidate}
              onConfirmSell={() =>
                markListedMutation.mutate({
                  id: r.id,
                  data: { listedPrice },
                })
              }
            />
          );
        },
      }),
      columnHelper.display({
        id: "listing",
        size: 100,
        header: "Listing",
        cell: ({ row }) => <ListingCell row={row.original} />,
      }),
      columnHelper.display({
        id: "broadcast",
        size: 84,
        header: "Broadcast",
        cell: ({ row }) => <BroadcastCell row={row.original} />,
      }),
      columnHelper.display({
        id: "qr",
        size: 40,
        header: () => (
          <span className="flex items-center justify-center" title="QR / PDF assets">
            <QrCode className="h-3.5 w-3.5" />
          </span>
        ),
        cell: ({ row }) => (
          <div className="flex justify-center">
            <QrCoverageCell row={row.original} />
          </div>
        ),
      }),
      columnHelper.display({
        id: "link",
        size: 40,
        header: () => (
          <span className="flex items-center justify-center" title="Transfer links">
            <Link2 className="h-3.5 w-3.5" />
          </span>
        ),
        cell: ({ row }) => (
          <div className="flex justify-center">
            <LinkCoverageCell row={row.original} />
          </div>
        ),
      }),
      columnHelper.display({
        id: "sales",
        size: 120,
        header: "Tickets",
        cell: ({ row }) => <TicketsCell listings={[row.original]} />,
      }),
      columnHelper.accessor((r) => r.vendorAccountEmail ?? "", {
        id: "account",
        header: "Account",
        size: 220,
        cell: ({ row }) => {
          const r = row.original;
          return (
            <AccountInfoPopover
              vendorAccountEmail={r.vendorAccountEmail}
              tmPassword={r.tmPassword}
              phoneOnFile={r.phoneOnFile}
              phoneStatus={r.phoneStatus}
              pullerName={r.pullerName}
            />
          );
        },
      }),
      columnHelper.accessor((r) => r.tmPassword ?? "", {
        id: "tmPassword",
        header: "PW",
        size: 120,
        cell: ({ row }) => {
          const pw = row.original.tmPassword;
          if (!pw) return <span className="text-muted-foreground/50 text-xs">—</span>;
          return <PwCell value={pw} />;
        },
      }),
      columnHelper.accessor((r) => r.pullerName ?? "", {
        id: "puller",
        header: "Puller",
        size: 130,
        cell: (info) => {
          const name = info.getValue() as string;
          return name ? (
            <div className="text-xs whitespace-nowrap truncate" title={name}>
              {name}
            </div>
          ) : (
            <span className="text-muted-foreground/50 text-xs">—</span>
          );
        },
      }),
      columnHelper.accessor((r) => r.vendorAccountEmail ?? "", {
        id: "origAcct",
        header: "Orig Acct",
        size: 180,
        cell: (info) => (
          <div
            className="text-xs whitespace-nowrap truncate"
            title={info.getValue() as string}
          >
            {(info.getValue() as string) || "-"}
          </div>
        ),
      }),
      columnHelper.display({
        id: "poChain",
        header: "PO › SALE › BB",
        size: 200,
        cell: ({ row }) => (
          <PoChainCell row={row.original} invalidate={invalidate} />
        ),
      }),
      columnHelper.accessor((r) => r.phoneStatus ?? "unknown", {
        id: "phone",
        header: "Phone",
        size: 56,
        cell: ({ row }) => {
          const r = row.original;
          return (
            <PhoneStatusIcon
              status={r.phoneStatus as "good" | "bad" | "unknown" | null}
              lastSmsTime={r.lastSmsTime}
              phoneType={r.phoneType}
              phone={r.phoneOnFile}
            />
          );
        },
      }),
      columnHelper.accessor((r) => r.bankMappingStatus ?? "", {
        id: "bank",
        header: "Bank",
        size: 150,
        cell: ({ row }) => {
          const r = row.original;
          if (!r.bankMappingStatus) {
            return (
              <Badge
                variant="outline"
                className="bg-amber-500/10 text-amber-500 border-amber-500/30 font-mono text-[9px] uppercase"
              >
                No Bank
              </Badge>
            );
          }
          const label = r.bankCardName || r.bankName || r.bankSource || "Bank";
          return (
            <BankNumbersPopover
              details={{
                label,
                variant: r.bankVariant,
                routingNumber: r.bankRouting,
                accountNumber: r.bankAccount,
                routingLast4: r.bankRoutingLast4,
                accountLast4: r.bankAccountLast4,
                variantId: r.bankVariantId,
                mappingStatus: r.bankMappingStatus,
                accountEmail: r.vendorAccountEmail,
              }}
            />
          );
        },
      }),
      columnHelper.display({
        id: "note",
        header: "Note",
        size: 120,
        cell: ({ row }) => {
          const r = row.original;
          return (
            <NoteCell
              row={r}
              onSave={(note) =>
                manualFields.mutateAsync({ id: r.id, data: { internalNotes: note ?? "" } })
              }
            />
          );
        },
      }),
    ].filter((col) => {
      if (col.id === "qr") return showQrColumn;
      if (col.id === "link") return showLinkColumn;
      // INTENT column only appears for buyback events.
      if (col.id === "intent") return showBuybackColumn;
      // In buyback-display mode the Margin / Get-In column is hidden entirely;
      // intent (Buy Back / Sell) is set from the status pill instead.
      if (col.id === "margin") return showBuybackColumn && !hideGetIn;
      // `origAcct` and `poChain` only make sense for buyback events when
      // individual rows are shown (i.e. not in purchase aggregate mode).
      // In "both" mode rows are also purchase aggregates, so hide them there too.
      if (col.id === "origAcct" || col.id === "poChain")
        return showBuybackColumn && groupBy !== "purchase" && groupBy !== "both";
      // `seatsChain` replaces `seats` + `poChain` in any purchase-aggregate view
      // ("purchase" only, or "both" where each row is still a purchase aggregate).
      if (col.id === "seatsChain")
        return showBuybackColumn && (groupBy === "purchase" || groupBy === "both");
      // `seats` is hidden whenever rows are purchase aggregates.
      if (col.id === "seats")
        return !(showBuybackColumn && (groupBy === "purchase" || groupBy === "both"));
      return true;
    }),
    [data, selectedIds, allSelected, busyId, defaultHoldDays, isAdmin, myPullerTag, fve, showQrColumn, showLinkColumn, showBuybackColumn, hideGetIn, groupBy],
  );

  // Merge saved column order with the currently-available columns. Conditional
  // columns (qr/link/intent/margin/etc.) are spliced into their natural position
  // from DEFAULT_COLUMN_ORDER so a stale saved order never drops or duplicates them.
  const effectiveColumnOrder = useMemo<ColumnOrderState>(() => {
    const availableIds = columns.map((c) => c.id as string);
    const availableSet = new Set(availableIds);
    const defaultOrder = DEFAULT_COLUMN_ORDER.filter((id) => availableSet.has(id));

    if (savedColumnOrder.length === 0) return defaultOrder;

    // Filter saved order to only currently-available columns.
    const saved = savedColumnOrder.filter((id) => availableSet.has(id));
    const savedSet = new Set(saved);
    const missing = defaultOrder.filter((id) => !savedSet.has(id));

    if (missing.length === 0) return saved;

    // Splice each missing column after the last saved entry that precedes it
    // in the canonical default order.
    const result = [...saved];
    for (const missingId of missing) {
      const defaultIdx = defaultOrder.indexOf(missingId);
      let insertAt = 0;
      for (let i = 0; i < result.length; i++) {
        const idx = defaultOrder.indexOf(result[i]);
        if (idx !== -1 && idx < defaultIdx) insertAt = i + 1;
      }
      result.splice(insertAt, 0, missingId);
    }
    return result;
  }, [columns, savedColumnOrder]);

  // dnd-kit sensors: require 5px of movement before activating drag to avoid
  // competing with sort-on-click.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const [activeDragColumnId, setActiveDragColumnId] = useState<string | null>(null);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragColumnId(event.active.id as string);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveDragColumnId(null);
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const fromIdx = effectiveColumnOrder.indexOf(active.id as string);
      const toIdx = effectiveColumnOrder.indexOf(over.id as string);
      if (fromIdx === -1 || toIdx === -1) return;
      setSavedColumnOrder(arrayMove(effectiveColumnOrder, fromIdx, toIdx));
    },
    [effectiveColumnOrder, setSavedColumnOrder],
  );

  const [activePickerColumnId, setActivePickerColumnId] = useState<string | null>(null);

  const handlePickerDragStart = useCallback((event: DragStartEvent) => {
    setActivePickerColumnId(event.active.id as string);
  }, []);

  const handlePickerDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActivePickerColumnId(null);
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const fromIdx = effectiveColumnOrder.indexOf(active.id as string);
      const toIdx = effectiveColumnOrder.indexOf(over.id as string);
      if (fromIdx === -1 || toIdx === -1) return;
      setSavedColumnOrder(arrayMove(effectiveColumnOrder, fromIdx, toIdx));
    },
    [effectiveColumnOrder, setSavedColumnOrder],
  );

  const buybackTargetPOs = useMemo<Set<string>>(() => {
    const s = new Set<string>();
    for (const r of data) {
      const bbPo = String(r.buybackBbPurchaseOrderId ?? "").trim();
      if (bbPo) s.add(bbPo);
    }
    return s;
  }, [data]);

  const filteredData = useMemo<InventoryListing[]>(
    () =>
      buybackTargetPOs.size === 0
        ? data
        : data.filter((r) => {
            const po = String(r.purchaseOrderId ?? "").trim();
            return !po || !buybackTargetPOs.has(po);
          }),
    [data, buybackTargetPOs],
  );

  const table = useReactTable({
    data: filteredData,
    columns,
    state: { sorting, columnVisibility, columnOrder: effectiveColumnOrder },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setSavedColumnOrder,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const sortedRows = table.getRowModel().rows;
  const visibleColumns = table.getVisibleLeafColumns();
  const { template: gridTemplate, minWidth: gridMinWidth } = useMemo(
    () => resizable.getLayout(visibleColumns.map((c) => c.id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleColumns.map((c) => c.id).join(","), columnVisibility, effectiveColumnOrder, resizable.getLayout],
  );

  // Keep the header horizontally aligned with the body when columns overflow
  // the panel (the body owns the horizontal scrollbar; the header mirrors it).
  const headerScrollRef = useRef<HTMLDivElement>(null);
  const syncHeaderScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    if (headerScrollRef.current) {
      headerScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
  }, []);

  // In "purchase" mode each PO becomes one flat aggregate row. Delegate to the
  // module-level buildPurchaseRows helper (which is also used per-puller in
  // "both" mode via bothPurchasesByPuller below).
  const purchaseRows = useMemo<PurchaseRow[]>(
    () => (groupBy === "purchase" ? buildPurchaseRows(filteredData) : []),
    [groupBy, filteredData],
  );

  // Group raw rows by puller -> renter. Leaf order respects the active column
  // sort when one is set, otherwise falls back to natural section/row/seat sort.
  // Used in "puller" and "both" modes. Not used in "purchase" or "flat".
  const groups = useMemo<PullerGroup[]>(() => {
    if (groupBy === "purchase" || groupBy === "flat") return [];

    const orderIndex = new Map<number, number>();
    sortedRows.forEach((r, i) => orderIndex.set(r.original.id, i));
    const sortingActive = sorting.length > 0;

    const pullerMap = new Map<string, Map<string, InventoryListing[]>>();
    for (const r of filteredData) {
      const pKey = r.pullerName?.trim() || UNASSIGNED_PULLER;
      const rKey = r.historicalJuiceRenterName?.trim() || NO_RENTER;
      let renters = pullerMap.get(pKey);
      if (!renters) {
        renters = new Map();
        pullerMap.set(pKey, renters);
      }
      const arr = renters.get(rKey);
      if (arr) arr.push(r);
      else renters.set(rKey, [r]);
    }

    const sortLeaf = (rows: InventoryListing[]) =>
      sortingActive
        ? [...rows].sort(
            (a, b) => (orderIndex.get(a.id) ?? 0) - (orderIndex.get(b.id) ?? 0),
          )
        : [...rows].sort(naturalSeatCompare);

    const orderNames = (names: string[], last: string) =>
      names.sort((a, b) => {
        if (a === last && b !== last) return 1;
        if (b === last && a !== last) return -1;
        return nat(a, b);
      });

    const result: PullerGroup[] = [];
    for (const pKey of orderNames([...pullerMap.keys()], UNASSIGNED_PULLER)) {
      const rentersMap = pullerMap.get(pKey)!;
      const renters: RenterGroup[] = [];
      const metrics: PullerMetrics = {
        listings: 0,
        seats: 0,
        held: 0,
        good: 0,
        bad: 0,
      };
      const pullerIds: number[] = [];
      for (const rKey of orderNames([...rentersMap.keys()], NO_RENTER)) {
        const leafRows = sortLeaf(rentersMap.get(rKey)!);
        const ids = leafRows.map((r) => r.id);
        pullerIds.push(...ids);
        for (const r of leafRows) {
          metrics.listings += 1;
          metrics.seats += displayQty(r);
          if (r.adminHoldActive) metrics.held += 1;
          if (r.phoneStatus === "good") metrics.good += 1;
          else if (r.phoneStatus === "bad") metrics.bad += 1;
        }
        renters.push({
          key: `${pKey}|||${rKey}`,
          name: rKey,
          ids,
          rows: leafRows,
        });
      }
      result.push({
        key: pKey,
        name: pKey,
        metrics,
        ids: pullerIds,
        renters,
      });
    }

    return result;
  }, [filteredData, sortedRows, sorting, groupBy]);

  const rowById = useMemo(() => {
    const m = new Map<number, Row<InventoryListing>>();
    sortedRows.forEach((r) => m.set(r.original.id, r));
    return m;
  }, [sortedRows]);

  // "both" mode: for each puller group, compute purchase sub-groups per renter.
  // Two-level map: pullerKey → renterName → PurchaseRow[].
  // Preserves the renter ordering from groups so the flatlist matches puller mode.
  const bothPurchasesByPullerRenter = useMemo<Map<string, Map<string, PurchaseRow[]>>>(() => {
    if (groupBy !== "both") return new Map();
    const result = new Map<string, Map<string, PurchaseRow[]>>();
    for (const puller of groups) {
      const renterMap = new Map<string, PurchaseRow[]>();
      for (const renter of puller.renters) {
        renterMap.set(renter.name, buildPurchaseRows(renter.rows));
      }
      result.set(puller.key, renterMap);
    }
    return result;
  }, [groupBy, groups]);

  // Flatten groups into a single virtualizable list, honoring collapse state.
  const { items, stickyIndexes } = useMemo(() => {
    // "purchase" mode: one aggregate row per purchase order, no grouping headers.
    if (groupBy === "purchase") {
      const list: FlatItem[] = purchaseRows.map((pr) => ({
        kind: "purchase" as const,
        key: `pr:${pr.purchaseOrderId}`,
        pr,
      }));
      return { items: list, stickyIndexes: [] };
    }

    // "flat" mode: every listing row rendered directly, no group headers.
    if (groupBy === "flat") {
      const list: FlatItem[] = [];
      for (const row of sortedRows) {
        list.push({ kind: "row", key: `row:${row.original.id}`, row });
      }
      return { items: list, stickyIndexes: [] };
    }

    // "both" mode: puller header → renter sub-header → purchase rows.
    if (groupBy === "both") {
      const list: FlatItem[] = [];
      const sticky: number[] = [];
      const singlePuller = groups.length === 1;
      for (const puller of groups) {
        if (!singlePuller) {
          sticky.push(list.length);
          list.push({ kind: "puller", key: `p:${puller.key}`, group: puller });
          if (collapsedPullers.has(puller.key)) continue;
        }
        const renterMap = bothPurchasesByPullerRenter.get(puller.key) ?? new Map();
        // Iterate renters in the same order as the puller group.
        // Every renter — including "No Renter" — gets a collapsible sub-header.
        for (const renter of puller.renters) {
          const purchaseSubs = renterMap.get(renter.name) ?? [];
          if (purchaseSubs.length === 0) continue;
          if (singlePuller) sticky.push(list.length);
          list.push({
            kind: "renter",
            key: `r:${renter.key}`,
            pullerKey: puller.key,
            group: renter,
          });
          if (collapsedRenters.has(renter.key)) continue;
          for (const pr of purchaseSubs) {
            list.push({
              kind: "purchase",
              key: `pp:${puller.key}|||renter:${renter.name}|||po:${pr.purchaseOrderId}`,
              pr,
            });
          }
        }
      }
      return { items: list, stickyIndexes: sticky };
    }

    // "puller" mode: puller header → renter sub-header → individual rows.
    const list: FlatItem[] = [];
    const sticky: number[] = [];
    const singlePuller = groups.length === 1;
    for (const puller of groups) {
      if (!singlePuller) {
        sticky.push(list.length);
        list.push({ kind: "puller", key: `p:${puller.key}`, group: puller });
        if (collapsedPullers.has(puller.key)) continue;
      }
      for (const renter of puller.renters) {
        const noRenter = renter.name === NO_RENTER;
        if (noRenter) {
          for (const r of renter.rows) {
            const row = rowById.get(r.id);
            if (row) list.push({ kind: "row", key: `row:${r.id}`, row });
          }
          continue;
        }
        if (singlePuller) sticky.push(list.length);
        list.push({
          kind: "renter",
          key: `r:${renter.key}`,
          pullerKey: puller.key,
          group: renter,
        });
        if (collapsedRenters.has(renter.key)) continue;
        for (const r of renter.rows) {
          const row = rowById.get(r.id);
          if (row) list.push({ kind: "row", key: `row:${r.id}`, row });
        }
      }
    }
    return { items: list, stickyIndexes: sticky };
  }, [groupBy, purchaseRows, groups, bothPurchasesByPullerRenter, collapsedPullers, collapsedRenters, rowById, sortedRows]);

  const parentRef = useRef<HTMLDivElement>(null);

  const activeStickyIndexRef = useRef<number | null>(null);
  const isSticky = useCallback(
    (index: number) => stickyIndexes.includes(index),
    [stickyIndexes],
  );
  const isActiveSticky = (index: number) =>
    activeStickyIndexRef.current !== null &&
    activeStickyIndexRef.current === index;

  const rowVirtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) => {
      const it = items[index];
      if (!it) return ROW_HEIGHT;
      if (it.kind === "puller") return PULLER_HEIGHT;
      if (it.kind === "renter") return RENTER_HEIGHT;
      // Purchase flat rows grow with listing count: base + extra lines per listing.
      // This covers both "purchase" mode and "both" mode (same aggregate row shape).
      if (it.kind === "purchase") return Math.max(ROW_HEIGHT, 12 + it.pr.listingCount * 24);
      return ROW_HEIGHT;
    },
    getItemKey: (index) => items[index]?.key ?? index,
    overscan: 12,
    rangeExtractor: useCallback(
      (range: { startIndex: number; endIndex: number; overscan: number; count: number }) => {
        let active: number | null = null;
        for (const i of stickyIndexes) {
          if (i <= range.startIndex) active = i;
          else break;
        }
        activeStickyIndexRef.current = active;
        const base = defaultRangeExtractor(range);
        const next = new Set(active !== null ? [active, ...base] : base);
        return [...next].sort((a, b) => a - b);
      },
      [stickyIndexes],
    ),
  });

  // Collapsing/expanding shifts item indices and sizes; remeasure so the
  // virtualizer doesn't keep stale offsets.
  useEffect(() => {
    rowVirtualizer.measure();
  }, [items, rowVirtualizer]);

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  const hideableColumns = useMemo(() => {
    const all = table.getAllLeafColumns().filter((c) => c.getCanHide());
    return [...all].sort((a, b) => {
      const ai = effectiveColumnOrder.indexOf(a.id);
      const bi = effectiveColumnOrder.indexOf(b.id);
      if (ai === -1 && bi === -1) return 0;
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
  }, [table, effectiveColumnOrder]);

  const groupSelectionState = (ids: number[]): boolean | "indeterminate" => {
    if (ids.length === 0) return false;
    let sel = 0;
    for (const id of ids) if (selectedIds.has(id)) sel += 1;
    if (sel === 0) return false;
    if (sel === ids.length) return true;
    return "indeterminate";
  };

  if (isMobile) {
    return (
      <div className="h-full flex flex-col overflow-hidden">
        <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-border/40 shrink-0">
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <Checkbox
              checked={allSelected && data.length > 0}
              onCheckedChange={onToggleSelectAll}
              aria-label="Select all"
            />
            Select all
          </label>
          <span className="text-[11px] font-mono text-muted-foreground">
            {data.length} {data.length === 1 ? "listing" : "listings"}
          </span>
        </div>
        <div className="flex-1 overflow-auto custom-scrollbar p-2 space-y-2">
          {items.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground text-sm">
              No inventory listings match the current filters.
            </div>
          ) : (
            items.map((item) => {
              if (item.kind === "puller") {
                const g = item.group;
                const isCollapsed = collapsedPullers.has(g.key);
                const selState = groupSelectionState(g.ids);
                return (
                  <div
                    key={item.key}
                    className="group/row flex flex-wrap items-center gap-2 rounded-md bg-panel border border-border px-3 py-2"
                  >
                    <button
                      onClick={() => togglePuller(g.key)}
                      className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
                      aria-label={isCollapsed ? "Expand puller" : "Collapse puller"}
                    >
                      {isCollapsed ? (
                        <ChevronRight className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </button>
                    <Checkbox
                      checked={selState}
                      onCheckedChange={() => onSelectIds(g.ids, selState !== true)}
                      aria-label={`Select all in ${g.name}`}
                      className="shrink-0 opacity-0 group-hover/row:opacity-100 data-[state=checked]:opacity-100 data-[state=indeterminate]:opacity-100 transition-opacity"
                    />
                    <span
                      className={cn(
                        "text-sm font-medium truncate",
                        g.key === UNASSIGNED_PULLER
                          ? "text-muted-foreground italic"
                          : "text-foreground",
                      )}
                      title={g.name}
                    >
                      {g.name}
                    </span>
                    <div className="ml-auto flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground shrink-0">
                      <span>
                        {g.metrics.listings} lst
                        <span className="text-muted-foreground/50"> · </span>
                        {g.metrics.seats} st
                      </span>
                      {g.metrics.held > 0 && (
                        <span className="text-amber-500">{g.metrics.held} held</span>
                      )}
                      <span title="Good / bad phones">
                        <span className="text-green-400">{g.metrics.good}</span>
                        <span className="text-muted-foreground/50">/</span>
                        <span className="text-red-400">{g.metrics.bad}</span>
                      </span>
                    </div>
                  </div>
                );
              }

              if (item.kind === "renter") {
                const g = item.group;
                const isCollapsed = collapsedRenters.has(g.key);
                const selState = groupSelectionState(g.ids);
                return (
                  <div
                    key={item.key}
                    className="group/row flex items-center gap-2 rounded-md bg-card/60 border border-border/40 pl-3 pr-3 py-1.5"
                  >
                    <button
                      onClick={() => toggleRenter(g.key)}
                      className="text-muted-foreground/70 hover:text-foreground transition-colors shrink-0"
                      aria-label={isCollapsed ? "Expand renter" : "Collapse renter"}
                    >
                      {isCollapsed ? (
                        <ChevronRight className="h-3.5 w-3.5" />
                      ) : (
                        <ChevronDown className="h-3.5 w-3.5" />
                      )}
                    </button>
                    <Checkbox
                      checked={selState}
                      onCheckedChange={() => onSelectIds(g.ids, selState !== true)}
                      aria-label={`Select all in ${g.name}`}
                      className="h-3.5 w-3.5 shrink-0 opacity-0 group-hover/row:opacity-100 data-[state=checked]:opacity-100 data-[state=indeterminate]:opacity-100 transition-opacity"
                    />
                    <span
                      className={cn(
                        "text-xs truncate",
                        g.name === NO_RENTER
                          ? "text-muted-foreground/70 italic"
                          : "text-muted-foreground",
                      )}
                      title={g.name}
                    >
                      {g.name}
                    </span>
                    <span className="ml-auto font-mono text-[10px] text-muted-foreground/60 shrink-0">
                      {g.ids.length}
                    </span>
                  </div>
                );
              }

              if (item.kind === "purchase") {
                const { pr } = item;
                const selState = groupSelectionState(pr.listingIds);
                return (
                  <div
                    key={item.key}
                    className="flex flex-col gap-1 rounded-md bg-panel border border-border px-3 py-2"
                  >
                    <div className="group/row flex items-center gap-2">
                      <Checkbox
                        checked={selState}
                        onCheckedChange={() => onSelectIds(pr.listingIds, selState !== true)}
                        aria-label={`Select all in ${pr.label}`}
                        className="shrink-0 opacity-0 group-hover/row:opacity-100 data-[state=checked]:opacity-100 data-[state=indeterminate]:opacity-100 transition-opacity"
                      />
                      <span className="text-sm font-medium truncate flex-1 min-w-0" title={pr.label}>
                        {pr.label}
                      </span>
                      <span className="font-mono text-[10px] text-muted-foreground shrink-0">
                        {pr.totalSeats} {pr.totalSeats === 1 ? "ticket" : "tickets"}
                        {" · "}
                        {pr.listingCount} {pr.listingCount === 1 ? "listing" : "listings"}
                      </span>
                    </div>
                    <SeatsChainCell listings={pr.listings} invalidate={invalidate} />
                  </div>
                );
              }

              const r = item.row.original;
              return (
                <InventoryMobileCard
                  key={item.key}
                  row={r}
                  fve={fve}
                  hideGetIn={hideGetIn}
                  defaultHoldDays={defaultHoldDays}
                  isBusy={busyId === r.id}
                  onHold={(days) => placeHold(r.id, days)}
                  onUnhold={() => releaseHold(r.id)}
                  isAdmin={isAdmin}
                  canAcknowledge={myPullerTag != null && myPullerTag === r.pullerTag}
                  onSavePrice={(price) =>
                    setRecommended.mutateAsync({
                      id: r.id,
                      data: { recommendedListPrice: price },
                    })
                  }
                  onAcknowledge={() => acknowledgeRecommended.mutateAsync({ id: r.id })}
                  selected={selectedIds.has(r.id)}
                  onToggleSelect={() => onSelectIds([r.id], !selectedIds.has(r.id))}
                  onSaveNote={(note) =>
                    manualFields.mutateAsync({
                      id: r.id,
                      data: { internalNotes: note ?? "" },
                    })
                  }
                  extraFields={[
                    {
                      label: "Account",
                      node: (
                        <span className="break-all text-xs">
                          {r.vendorAccountEmail || "-"}
                        </span>
                      ),
                      full: true,
                    },
                  ]}
                  showListing
                  showSales
                  showPhone
                  showBank
                  showQr
                  showLink
                />
              );
            })
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex items-center justify-end gap-1 px-2 py-1 border-b border-border/40 shrink-0">
        {savedColumnOrder.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 text-[11px] px-2 text-muted-foreground hover:text-foreground"
            onClick={() => setSavedColumnOrder([])}
            title="Reset column order to default"
          >
            <RotateCcw className="h-3 w-3 mr-1" /> Reset order
          </Button>
        )}
        <ResetColumnWidthsButton
          show={resizable.isCustomized}
          onReset={resizable.reset}
        />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-[11px] px-2 text-muted-foreground hover:text-foreground"
            >
              <SlidersHorizontal className="h-3.5 w-3.5 mr-1" />
              Columns
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuLabel className="text-[11px]">Toggle columns</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handlePickerDragStart}
              onDragEnd={handlePickerDragEnd}
            >
              <SortableContext
                items={hideableColumns.map((c) => c.id)}
                strategy={verticalListSortingStrategy}
              >
                {hideableColumns.map((col) => {
                  const label =
                    typeof col.columnDef.header === "string"
                      ? col.columnDef.header
                      : col.id;
                  return (
                    <SortablePickerItem
                      key={col.id}
                      id={col.id}
                      label={label}
                      checked={col.getIsVisible()}
                      onCheckedChange={(v) => col.toggleVisibility(!!v)}
                    />
                  );
                })}
              </SortableContext>
              <DragOverlay dropAnimation={null}>
                {activePickerColumnId ? (
                  <div className="flex items-center gap-1.5 px-7 py-1.5 text-xs bg-background border border-border rounded shadow-lg opacity-90">
                    <GripVertical className="h-3 w-3 text-muted-foreground/60 shrink-0" />
                    {(() => {
                      const col = hideableColumns.find((c) => c.id === activePickerColumnId);
                      return col
                        ? typeof col.columnDef.header === "string"
                          ? col.columnDef.header
                          : col.id
                        : activePickerColumnId;
                    })()}
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Column header (outside the scroll region so it never scrolls away).
          Horizontally it mirrors the body's scroll so columns stay aligned.
          DndContext wraps the header row so columns can be drag-reordered. */}
      <div ref={headerScrollRef} className="overflow-hidden shrink-0">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={visibleColumns.map((c) => c.id)}
            strategy={horizontalListSortingStrategy}
          >
            <div
              className="grid bg-panel border-b border-border"
              style={{ gridTemplateColumns: gridTemplate, minWidth: gridMinWidth, height: HEADER_HEIGHT }}
            >
              {table.getHeaderGroups()[0]?.headers.map((header) => (
                <SortableHeaderCell
                  key={header.id}
                  header={header}
                  resizable={resizable}
                />
              ))}
            </div>
          </SortableContext>
          <DragOverlay dropAnimation={null}>
            {activeDragColumnId ? (
              <div className="flex items-center px-3 text-[10px] uppercase tracking-wider text-muted-foreground font-mono font-medium whitespace-nowrap bg-panel border border-primary/40 rounded shadow-lg opacity-90 h-[36px]">
                {activeDragColumnId}
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>

      <div
        ref={parentRef}
        className="flex-1 overflow-auto custom-scrollbar"
        onScroll={syncHeaderScroll}
      >
        {items.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground text-sm">
            No inventory listings match the current filters.
          </div>
        ) : (
          <div style={{ height: totalSize, position: "relative", minWidth: gridMinWidth }}>
            {virtualRows.map((virtualRow) => {
              const item = items[virtualRow.index];
              if (!item) return null;
              const sticky = isSticky(virtualRow.index);
              const active = isActiveSticky(virtualRow.index);
              const style: React.CSSProperties = {
                position: active ? "sticky" : "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: virtualRow.size,
                zIndex: sticky ? 11 : 1,
                ...(active
                  ? {}
                  : { transform: `translateY(${virtualRow.start}px)` }),
              };

              if (item.kind === "puller") {
                const g = item.group;
                const collapsed = collapsedPullers.has(g.key);
                const selState = groupSelectionState(g.ids);
                return (
                  <div
                    key={virtualRow.key}
                    style={style}
                    className="group/row flex items-center gap-2.5 px-3 bg-panel border-y border-border"
                  >
                    <button
                      onClick={() => togglePuller(g.key)}
                      className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
                      aria-label={collapsed ? "Expand puller" : "Collapse puller"}
                    >
                      {collapsed ? (
                        <ChevronRight className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </button>
                    <Checkbox
                      checked={selState}
                      onCheckedChange={() =>
                        onSelectIds(g.ids, selState !== true)
                      }
                      aria-label={`Select all in ${g.name}`}
                      className="shrink-0 opacity-0 group-hover/row:opacity-100 data-[state=checked]:opacity-100 data-[state=indeterminate]:opacity-100 transition-opacity"
                    />
                    <span
                      className={cn(
                        "text-sm font-medium truncate",
                        g.key === UNASSIGNED_PULLER
                          ? "text-muted-foreground italic"
                          : "text-foreground",
                      )}
                      title={g.name}
                    >
                      {g.name}
                    </span>
                    <div className="ml-auto flex items-center gap-3 font-mono text-[10px] uppercase tracking-wider text-muted-foreground shrink-0">
                      <span>
                        {g.metrics.listings} lst
                        <span className="text-muted-foreground/50"> · </span>
                        {g.metrics.seats} st
                      </span>
                      {g.metrics.held > 0 && (
                        <span className="text-amber-500">{g.metrics.held} held</span>
                      )}
                      <span title="Good / bad phones">
                        <span className="text-green-400">{g.metrics.good}</span>
                        <span className="text-muted-foreground/50">/</span>
                        <span className="text-red-400">{g.metrics.bad}</span>
                      </span>
                    </div>
                  </div>
                );
              }

              if (item.kind === "renter") {
                const g = item.group;
                const collapsed = collapsedRenters.has(g.key);
                const selState = groupSelectionState(g.ids);
                return (
                  <div
                    key={virtualRow.key}
                    style={style}
                    className="group/row flex items-center gap-2 pl-9 pr-3 bg-card/60 border-b border-border/40"
                  >
                    <button
                      onClick={() => toggleRenter(g.key)}
                      className="text-muted-foreground/70 hover:text-foreground transition-colors shrink-0"
                      aria-label={collapsed ? "Expand renter" : "Collapse renter"}
                    >
                      {collapsed ? (
                        <ChevronRight className="h-3.5 w-3.5" />
                      ) : (
                        <ChevronDown className="h-3.5 w-3.5" />
                      )}
                    </button>
                    <Checkbox
                      checked={selState}
                      onCheckedChange={() =>
                        onSelectIds(g.ids, selState !== true)
                      }
                      aria-label={`Select all in ${g.name}`}
                      className="h-3.5 w-3.5 shrink-0 opacity-0 group-hover/row:opacity-100 data-[state=checked]:opacity-100 data-[state=indeterminate]:opacity-100 transition-opacity"
                    />
                    <span
                      className={cn(
                        "text-xs truncate",
                        g.name === NO_RENTER
                          ? "text-muted-foreground/70 italic"
                          : "text-muted-foreground",
                      )}
                      title={g.name}
                    >
                      {g.name}
                    </span>
                    <span className="font-mono text-[10px] text-muted-foreground/60 shrink-0">
                      {g.ids.length}
                    </span>
                  </div>
                );
              }

              if (item.kind === "purchase") {
                const { pr } = item;
                const repId = pr.representativeListing?.id;
                const repRow = repId != null ? rowById.get(repId) : undefined;
                if (!repRow) return null;
                const selState = groupSelectionState(pr.listingIds);
                const anyListed = pr.listings.some((l) => l.listedOnFve);
                const allSelected = pr.listingIds.every((id) => selectedIds.has(id));
                return (
                  <div
                    key={virtualRow.key}
                    style={{ ...style, gridTemplateColumns: gridTemplate, display: "grid" }}
                    className={cn(
                      "group border-b border-border/40 transition-colors items-start pt-1.5",
                      allSelected
                        ? "bg-primary/5"
                        : anyListed
                          ? "bg-emerald-500/[0.05] hover:bg-emerald-500/[0.10]"
                          : "hover:bg-muted/30",
                    )}
                  >
                    {repRow.getVisibleCells().map((cell) => (
                      <div
                        key={cell.id}
                        className="px-3 overflow-hidden flex items-start"
                      >
                        {cell.column.id === "select" ? (
                          <Checkbox
                            checked={selState}
                            onCheckedChange={() => onSelectIds(pr.listingIds, selState !== true)}
                            aria-label={`Select all in ${pr.label}`}
                            className="opacity-0 group-hover:opacity-100 data-[state=checked]:opacity-100 data-[state=indeterminate]:opacity-100 transition-opacity"
                          />
                        ) : cell.column.id === "seatsChain" ? (
                          <SeatsChainCell
                            listings={pr.listings}
                            invalidate={invalidate}
                          />
                        ) : cell.column.id === "sales" ? (
                          <TicketsCell listings={pr.listings} />
                        ) : cell.column.id === "qty" ? (
                          <span className="font-mono text-xs">{pr.totalSeats}</span>
                        ) : (
                          flexRender(cell.column.columnDef.cell, cell.getContext())
                        )}
                      </div>
                    ))}
                  </div>
                );
              }

              const row = item.row;
              const selected = selectedIds.has(row.original.id);
              const isListedOnFve = !!row.original.listedOnFve;
              return (
                <div
                  key={virtualRow.key}
                  style={{ ...style, gridTemplateColumns: gridTemplate, display: "grid" }}
                  className={cn(
                    "group border-b border-border/40 transition-colors items-center",
                    selected
                      ? "bg-primary/5"
                      : isListedOnFve
                        ? "bg-emerald-500/[0.07] hover:bg-emerald-500/[0.12]"
                        : "hover:bg-muted/40",
                  )}
                >
                  {row.getVisibleCells().map((cell) => (
                    <div
                      key={cell.id}
                      className="px-3 overflow-hidden flex items-center"
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
